import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import BrandLogo from "@/components/BrandLogo";
import {
  LogOut, LayoutDashboard, UtensilsCrossed, Users, ListOrdered, BarChart3, Wallet, Calendar, Home,
  Package, Receipt, UserCheck, PartyPopper, Store, Network, MoreHorizontal, QrCode, Settings, FileText, Sparkles, Building2, BadgeIndianRupee, Zap, UserCircle, ClipboardList, GraduationCap,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useState } from "react";

const NAV = {
  parent: [
    { to: "/parent", label: "Home", icon: Home },
    { to: "/parent/my-orders", label: "Orders", icon: UtensilsCrossed },
    { to: "/parent/instashop", label: "InstaShop", icon: Zap, requireInstashop: true },
    { to: "/parent/reports", label: "Consumption", icon: BarChart3 },
  ],
  admin: [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard },
    { to: "/admin/meal-plans", label: "Meal Plans", icon: Calendar },
    { to: "/admin/weekly-menu", label: "Weekly Menu", icon: FileText },
    { to: "/admin/consumption-log", label: "Consumption Log", icon: ClipboardList },
    { to: "/admin/item-sales", label: "Sales", icon: BadgeIndianRupee },
    { to: "/admin/menu", label: "InstaShop Menu", icon: UtensilsCrossed, requireInstashop: true },
    { to: "/admin/vendors", label: "Vendors", icon: Store, requireInstashop: true },
  ],
  adminMore: [
    { to: "/admin/students", label: "Students", icon: GraduationCap },
    { to: "/admin/staff", label: "Staff", icon: UserCheck },
    { to: "/admin/users", label: "All users", icon: Users },
  ],
  // Student variant retained for backward compat (RFID flows etc.) — points to parent nav.
  student: [
    { to: "/parent", label: "Home", icon: Home },
    { to: "/parent/my-orders", label: "Orders", icon: UtensilsCrossed },
    { to: "/parent/reports", label: "Consumption", icon: BarChart3 },
  ],
};

export const AppShell = ({ children, variant = "parent" }) => {
  const { user, logout, school } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const instashopOn = school?.instashop_enabled === true;
  const preorderOn = school?.preorder_enabled !== false;
  const mealPlanOn = school?.meal_plan_enabled !== false;
  // Filter nav items based on school config (hide InstaShop items for non-InstaShop schools)
  const items = (NAV[variant] || []).filter(it => {
    if (it.requireInstashop && !instashopOn) return false;
    if (it.to === "/parent/preorder" && !preorderOn) return false;
    return true;
  });
  const more = variant === "admin" ? NAV.adminMore : [];
  const [moreOpen, setMoreOpen] = useState(false);

  return (
    <div className="min-h-screen bg-cream warm-grain">
      <header className="sticky top-0 z-30 backdrop-blur-md bg-cream/80 border-b border-border">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2" data-testid="header-home-link">
            <BrandLogo size="md" />
            <span className="hidden sm:inline-block text-[11px] font-bold uppercase tracking-[0.2em] text-ink-500 ml-2 px-2 py-0.5 rounded-full bg-cream-200">
              {variant === "admin" ? "Admin Hub" : variant === "student" ? "Student" : "Parent"}
            </span>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {items.map((it) => {
              const Icon = it.icon;
              const active = location.pathname === it.to;
              return (
                <Link key={it.to} to={it.to}
                  data-testid={`nav-${it.label.toLowerCase().replace(/\s|&/g,'-')}`}
                  className={cn(
                    "px-3 py-2 rounded-full text-sm font-semibold transition-colors flex items-center gap-2",
                    active ? "bg-ink text-white" : "text-ink-700 hover:bg-cream-200"
                  )}>
                  <Icon size={16} strokeWidth={2.4} />
                  {it.label}
                </Link>
              );
            })}
            {more.length > 0 && (
              <div className="relative">
                <button data-testid="nav-more" onClick={() => setMoreOpen(o => !o)}
                  className={cn("px-3 py-2 rounded-full text-sm font-semibold flex items-center gap-2", more.some(m => location.pathname === m.to) ? "bg-ink text-white" : "text-ink-700 hover:bg-cream-200")}>
                  <MoreHorizontal size={16} /> More
                </button>
                {moreOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setMoreOpen(false)} />
                    <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-border rounded-2xl shadow-floating min-w-[220px] py-2">
                      {more.map(m => {
                        const Icon = m.icon;
                        const active = location.pathname === m.to;
                        return (
                          <Link key={m.to} to={m.to} onClick={() => setMoreOpen(false)}
                            data-testid={`nav-${m.label.toLowerCase().replace(/\s|&/g,'-')}`}
                            className={cn("block px-4 py-2 text-sm font-semibold flex items-center gap-2", active ? "text-brand" : "text-ink-700 hover:bg-cream-100")}>
                            <Icon size={14} /> {m.label}
                          </Link>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/profile" data-testid="nav-profile"
              className={cn("hidden sm:flex items-center gap-2 px-3 py-2 rounded-full text-sm font-semibold transition-colors",
                location.pathname === "/profile" ? "bg-ink text-white" : "text-ink-700 hover:bg-cream-200")}>
              <UserCircle size={16} strokeWidth={2.4} />
              <span>{user?.name}</span>
            </Link>
            <button data-testid="logout-btn" onClick={() => { logout(); navigate("/"); }}
              className="p-2 rounded-full hover:bg-cream-200 text-ink-700" title="Log out">
              <LogOut size={18} />
            </button>
          </div>
        </div>
        <nav className="md:hidden flex items-center gap-1 px-3 pb-2 overflow-x-auto no-scrollbar">
          {[...items, ...more].map((it) => {
            const active = location.pathname === it.to;
            return (
              <Link key={it.to} to={it.to}
                className={cn("px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap",
                  active ? "bg-ink text-white" : "bg-cream-200 text-ink-700")}>
                {it.label}
              </Link>
            );
          })}
          <Link to="/profile"
            className={cn("px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap",
              location.pathname === "/profile" ? "bg-ink text-white" : "bg-cream-200 text-ink-700")}>
            Profile
          </Link>
        </nav>
      </header>
      <main>{children}</main>
    </div>
  );
};

export default AppShell;
